import React, { useMemo, useState } from "react";
import {
  ARCHETYPE_DOT,
  ARCHETYPE_DOT_FALLBACK,
  DELIVERABLES,
  OFFER_RANGE,
  estCostRange,
  fmtMoneyCompact,
  fmtRange,
  fullName,
  offerStatus,
} from "../brandUtils";
import { Avatar, Badge, BmModal, Btn, Input } from "../ui/BrandUI";
import Icon from "../ui/icons";
import QtyStepper from "../ui/QtyStepper";

// Per-athlete deliverable quantities + manual offer, benchmarked against the
// estimated cost computed live from the athlete's NILx rate card.
const AthleteOfferModal = ({ item, onSave, onRemove, onClose }) => {
  const athlete = item.athlete;
  const [deliverables, setDeliverables] = useState({ ...item.deliverables });
  const [offer, setOffer] = useState(item.offer ?? "");

  const est = useMemo(
    () => estCostRange(athlete.nilRates, deliverables),
    [athlete.nilRates, deliverables],
  );
  const offerNum = offer === "" ? null : Number(String(offer).replace(/,/g, ""));
  const range = OFFER_RANGE[offerStatus(offerNum, est)];
  const subline = [athlete.position, athlete.school].filter(Boolean).join(" · ");

  const title = (
    <span className="flex items-start gap-4">
      <Avatar name={athlete.name} surname={athlete.surname} src={athlete.avatar} size={48} />
      <span className="flex min-w-0 flex-col gap-1">
        <span className="bm-h-m text-bm-ink">{fullName(athlete)}</span>
        {subline && <span className="bm-body-s text-bm-muted">{subline}</span>}
        {athlete.archetype && (
          <span className="mt-1">
            <Badge tone="outline" dot={ARCHETYPE_DOT[athlete.archetype] || ARCHETYPE_DOT_FALLBACK}>
              {athlete.archetype}
            </Badge>
          </span>
        )}
      </span>
    </span>
  );

  return (
    <BmModal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Btn variant="secondary" className="gap-2" onClick={onRemove}>
            <Icon name="x" size={14} />
            Remove
          </Btn>
          <Btn
            className="flex-1"
            onClick={() =>
              onSave({
                deliverables,
                offer: offerNum && offerNum > 0 ? offerNum : null,
              })
            }
          >
            Save
          </Btn>
        </>
      }
    >
      <p className="bm-body-s text-bm-muted">
        Reference &middot; CacheValue {fmtRange(athlete.cacheValue) || "n/a"} &middot; CacheScore {athlete.cacheScore}
        {athlete.nilx?.min ? <> &middot; NILx {fmtMoneyCompact(athlete.nilx.min)}</> : null}
      </p>

      <h3 className="mt-5 bm-h-s text-bm-ink">Deliverables</h3>
      <p className="mt-1 bm-body-s text-bm-muted">Set quantities per deliverable type.</p>

      <div className="mt-2">
        {DELIVERABLES.map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between gap-4 border-b border-bm-border py-3 last:border-b-0">
            <span className="bm-body-m text-bm-ink">{label}</span>
            <QtyStepper
              label={label}
              value={deliverables[key]}
              onChange={(v) => setDeliverables((d) => ({ ...d, [key]: v }))}
            />
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-4 border-t border-bm-border pt-4">
        <span className="bm-body-m text-bm-muted">Estimated cost</span>
        {est ? (
          <span className="bm-num-m text-bm-ink">{fmtRange(est, { compact: false })}</span>
        ) : (
          <span className="bm-label-l text-bm-tertiary">No deliverables selected</span>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-4">
        <span className="bm-body-m text-bm-muted">Your offer</span>
        <span className="w-44">
          <Input
            prefix="$"
            aria-label="Your offer"
            value={offer}
            onChange={(e) => setOffer(e.target.value.replace(/[^\d,]/g, ""))}
            placeholder="Enter amount"
            inputMode="numeric"
            className="text-right"
          />
        </span>
      </div>
      <div className="mt-3 flex justify-end">
        {range ? (
          <Badge tone="outline" dot={range.dot}>
            {range.label}
          </Badge>
        ) : (
          <span className="bm-body-s text-bm-muted">Add deliverables to benchmark this offer.</span>
        )}
      </div>
    </BmModal>
  );
};

export default AthleteOfferModal;
